import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  UserCog, Plus, Check, X, Edit2, Trash2, ShieldCheck, AlertCircle,
  Search, Minus, Clock, Calendar, Lock
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import PersonnelAgentRecap from "./PersonnelAgentRecap";
import PersonnelOrderActions from "./PersonnelOrderActions";
import BonsKpiHeader from "./BonsKpiHeader";
import BonsFilterChips from "./BonsFilterChips";
import BonsBulkActionBanner from "./BonsBulkActionBanner";
import BonBlockToggle, { BonBlockActiveBanner } from "./BonBlockToggle";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// 🆕 Fév 2026 : les plafonds et remises sont désormais lus dynamiquement depuis /api/admin/caisse-thresholds.
// Les constantes ci-dessous ne servent QUE de fallback ultime (si l'API échoue au chargement initial).
const FALLBACK_MAX_PER_MONTH = 25000;
const FALLBACK_DISCOUNT_RATE = 0.5;

const STATUS_BADGE = {
  pending_manager: { label: "Auto-confirmation Resp. Op. en attente", className: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  pending_director: { label: "En attente Directrice", className: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
  authorized: { label: "Autorisé", className: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  settled: { label: "Réglé sur salaire", className: "bg-slate-500/20 text-slate-300 border-slate-500/40" },
  cancelled: { label: "NON AUTORISÉ", className: "bg-red-500/25 text-red-200 border-red-500/60 font-bold" },
};

const monthKey = (d = new Date()) => format(d, "yyyy-MM");

const ManagerOrdersTab = ({ currentUser, formatPrice, products = [] }) => {
  const isAdmin = currentUser?.role === 'admin';
  const isManager = currentUser?.role === 'manager';
  const defaultManagerName = currentUser?.full_name || currentUser?.name || currentUser?.username || "Responsable Op. & Log";

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState(monthKey());
  // 🆕 Filtre chips du fil d'activité (all | pending | authorized | today)
  const [chipFilter, setChipFilter] = useState("all");
  // 🆕 État du blocage temporaire des demandes de bons
  const [bonBlock, setBonBlock] = useState({ active: false });

  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [employeeName, setEmployeeName] = useState(defaultManagerName);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [capStatus, setCapStatus] = useState(null);

  const [showClosureModal, setShowClosureModal] = useState(false);
  const [closurePreview, setClosurePreview] = useState(null);

  // 🆕 Seuils dynamiques
  const [maxPerMonth, setMaxPerMonth] = useState(FALLBACK_MAX_PER_MONTH);
  const [discountRate, setDiscountRate] = useState(FALLBACK_DISCOUNT_RATE);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/caisse-thresholds`);
        if (cancelled) return;
        const cap = Number(res.data?.manager_monthly_cap);
        const rate = Number(res.data?.manager_discount_rate);
        if (Number.isFinite(cap) && cap >= 0) setMaxPerMonth(cap);
        if (Number.isFinite(rate) && rate >= 0 && rate <= 1) setDiscountRate(rate);
      } catch (e) {
        console.warn("caisse-thresholds fetch fallback:", e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { fetchOrders(); }, [filterMonth]);

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API}/manager-orders`, { params: { month: filterMonth } });
      setOrders(res.data.orders || []);
      setStats(res.data.stats || {});
    } catch (e) { console.error("fetch manager-orders err", e); }
  };

  const fetchCap = async (name) => {
    if (!name?.trim()) { setCapStatus(null); return; }
    try {
      const res = await axios.get(`${API}/manager-orders/cap-status`, { params: { employee_name: name.trim(), month: monthKey() } });
      setCapStatus(res.data);
    } catch { setCapStatus(null); }
  };

  useEffect(() => {
    const t = setTimeout(() => fetchCap(employeeName), 300);
    return () => clearTimeout(t);
  }, [employeeName]);

  const resetForm = () => {
    setEditingOrder(null);
    setEmployeeName(defaultManagerName);
    setSearchTerm("");
    setSelectedItems([]);
    setNotes("");
  };

  const openCreateModal = () => { resetForm(); setShowModal(true); };

  const openEditModal = (order) => {
    if (order.status !== "pending_manager") {
      toast.error("Cette commande ne peut plus être modifiée");
      return;
    }
    setEditingOrder(order);
    setEmployeeName(order.employee_name || defaultManagerName);
    setSelectedItems(order.items || []);
    setNotes(order.notes || "");
    setShowModal(true);
  };

  const filteredProducts = products.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addProduct = (product) => {
    const existing = selectedItems.find(it => it.product_id === product.id);
    if (existing) {
      setSelectedItems(selectedItems.map(it => it.product_id === product.id ? { ...it, quantity: it.quantity + 1 } : it));
    } else {
      setSelectedItems([...selectedItems, { product_id: product.id, name: product.name, price: product.price, quantity: 1, category: product.category }]);
    }
  };

  const decreaseProduct = (productId) => {
    const existing = selectedItems.find(it => it.product_id === productId);
    if (existing && existing.quantity > 1) {
      setSelectedItems(selectedItems.map(it => it.product_id === productId ? { ...it, quantity: it.quantity - 1 } : it));
    } else {
      setSelectedItems(selectedItems.filter(it => it.product_id !== productId));
    }
  };

  const subtotal = useMemo(() => selectedItems.reduce((s, it) => s + (it.price * it.quantity), 0), [selectedItems]);
  const discount = subtotal * discountRate;
  const totalAfter = subtotal - discount;

  const projectedUsed = (capStatus?.used || 0) + (editingOrder ? -editingOrder.total : 0) + totalAfter;
  const willExceedCap = maxPerMonth > 0 && projectedUsed > maxPerMonth + 0.01;

  const handleSubmit = async () => {
    if (!employeeName.trim()) { toast.error("Nom de la responsable op. & log requis"); return; }
    if (selectedItems.length === 0) { toast.error("Au moins un article requis"); return; }
    if (willExceedCap) { toast.error(`Plafond mensuel dépassé (max ${formatPrice(maxPerMonth)} F/mois)`); return; }
    setLoading(true);
    try {
      const payload = {
        employee_name: employeeName.trim(),
        employee_position: "Responsable Op. & Log",
        items: selectedItems,
        notes,
        created_by: currentUser?.full_name || currentUser?.name || currentUser?.username || "Responsable Op. & Log",
      };
      if (editingOrder) {
        await axios.put(`${API}/manager-orders/${editingOrder.id}`, payload);
        toast.success("Commande modifiée");
      } else {
        await axios.post(`${API}/manager-orders`, payload);
        toast.success("Commande responsable op. & log enregistrée — auto-confirmation requise");
      }
      setShowModal(false);
      resetForm();
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setLoading(false); }
  };

  const authorize = async (order, role) => {
    const label = role === "manager" ? "Responsable Op. & Log (auto-confirmation)" : "la Direction";
    if (!window.confirm(`Autoriser cette commande responsable op. & log en tant que ${label} ?\n\nMontant à retenir : ${formatPrice(order.total)} F`)) return;
    try {
      await axios.put(`${API}/manager-orders/${order.id}/authorize`, {
        by_role: role,
        signer_name: currentUser?.full_name || currentUser?.name || currentUser?.username || label,
      });
      toast.success(`Autorisé par ${label}` + (role === "director" ? " — stock décrémenté" : ""));
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'autorisation"); }
  };

  const cancelOrder = async (order) => {
    // 🆕 Fév 2026 : capture le motif de refus/annulation pour tracer sur le profil Resp. Op.
    const reason = window.prompt(
      `Refuser / annuler ce bon Resp. Op. & Log ?\n\nMontant total des plats : ${formatPrice(order.subtotal || order.total)} F\n\nMotif du refus (ex: dépasse plafond, produit indisponible, doublon) :`,
      "Non autorisé"
    );
    if (reason === null) return; // Cancel prompt
    const trimmedReason = reason.trim() || "Non autorisé";
    try {
      await axios.delete(`${API}/manager-orders/${order.id}`, { params: { reason: trimmedReason } });
      toast.success(`Bon marqué NON AUTORISÉ — motif : ${trimmedReason}`);
      fetchOrders();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'annulation"); }
  };

  const previewClosure = () => {
    const target = filterMonth || monthKey();
    const eligible = orders.filter(o => o.month_period === target && o.status === "authorized");
    if (eligible.length === 0) { toast.warning("Aucune commande autorisée à clôturer"); return; }
    const byEmp = {};
    eligible.forEach(o => {
      const k = o.employee_name;
      if (!byEmp[k]) byEmp[k] = { name: k, position: o.employee_position, count: 0, total: 0 };
      byEmp[k].count += 1;
      byEmp[k].total += o.total;
    });
    setClosurePreview({
      month: target,
      total_count: eligible.length,
      total_amount: eligible.reduce((s, o) => s + o.total, 0),
      by_employee: Object.values(byEmp).sort((a, b) => a.name.localeCompare(b.name)),
    });
    setShowClosureModal(true);
  };

  const confirmClosure = async () => {
    if (!closurePreview) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/manager-orders/close-month`, {
        month: closurePreview.month,
        closed_by: currentUser?.full_name || currentUser?.name || "Admin",
      });
      toast.success(`Mois clôturé : ${res.data.settled_count} commande(s) déduite(s) du salaire`);
      setShowClosureModal(false);
      setClosurePreview(null);
      fetchOrders();
      window.open(`${API}/manager-orders/closure-pdf?month=${closurePreview.month}`, "_blank");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de clôture");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="manager-orders-tab">
      {/* 🆕 ZONE 1 · Bandeau KPI sticky (à retenir · en attente · non autorisé · mois) */}
      <BonsKpiHeader
        accent="violet"
        filterMonth={filterMonth}
        orders={orders}
        maxPerMonth={maxPerMonth}
        formatPrice={formatPrice}
        dataTestId="mgr-kpi-header"
        monthSlot={
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="text-lg sm:text-xl font-bold text-violet-300 flex items-center gap-2">
                <UserCog className="w-5 h-5 sm:w-6 sm:h-6" />
                Bons RESPONSABLE OP. & LOG
              </h2>
              <Badge className="bg-violet-500/30 text-violet-200 hidden sm:inline-flex">Crédit salaire · {formatPrice(maxPerMonth)} F/mois</Badge>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                type="month"
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white h-9 w-40"
                data-testid="mgr-filter-month"
              />
              <Button onClick={openCreateModal} className="bg-violet-600 hover:bg-violet-700 h-9" data-testid="mgr-create-btn" disabled={bonBlock.active}>
                <Plus className="w-4 h-4 mr-1" /> Nouvelle commande
              </Button>
              {/* Toujours monté pour synchroniser bonBlock ; le bouton se masque tout seul si !isAdmin */}
              <BonBlockToggle
                isAdmin={isAdmin}
                adminName={currentUser?.full_name || currentUser?.username || "Admin"}
                onChange={setBonBlock}
                dataTestId="mgr-bon-block"
              />
              {isAdmin && (
                <Button onClick={previewClosure} variant="outline" className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10 h-9" data-testid="mgr-close-month-btn">
                  <Lock className="w-4 h-4 mr-1" /> Clôturer le mois
                </Button>
              )}
            </div>
          </div>
        }
      />

      {/* 🆕 Bandeau ROUGE si le blocage est actif (visible pour TOUS) */}
      <BonBlockActiveBanner block={bonBlock} dataTestId="mgr-block-banner" />

      {/* 🆕 ZONE 3 (banner) · Alerte contextuelle si ≥3 bons en attente */}
      <BonsBulkActionBanner
        count={(stats.count_pending_manager || 0) + (stats.count_pending_director || 0)}
        total={stats.total_pending || 0}
        formatPrice={formatPrice}
        scopeLabel="bons Resp. Op."
        dataTestId="mgr-bulk-banner"
      />

      {/* 🆕 ZONE 2 · Récap par agent (bulles cliquables avec dot d'alerte) */}
      <PersonnelAgentRecap
        orders={orders}
        accent="violet"
        formatPrice={formatPrice}
        dataTestId="mgr-agent-recap"
        scope="manager"
        isAdmin={isAdmin}
        adminName={currentUser?.full_name || currentUser?.username || "Admin"}
        onRefresh={fetchOrders}
      />

      {/* 🆕 ZONE 3 (chips) · Filtres rapides du fil d'activité */}
      <BonsFilterChips
        value={chipFilter}
        onChange={setChipFilter}
        dataTestId="mgr-filter-chips"
        counts={{
          all: orders.filter(o => o.status !== "cancelled").length,
          pending: orders.filter(o => o.status === "pending_manager" || o.status === "pending_director").length,
          authorized: orders.filter(o => o.status === "authorized").length,
          today: orders.filter(o => (o.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10) && o.status !== "cancelled").length,
        }}
      />

      {orders.length === 0 ? (
        <Card className="bg-slate-800/40 border-slate-700">
          <CardContent className="p-8 text-center text-slate-400">
            <UserCog className="w-10 h-10 mx-auto mb-2 text-slate-600" />
            <p>Aucune commande responsable op. & log pour {filterMonth}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="mgr-orders-list">
          {/* 🆕 Les bons annulés (NON AUTORISÉ) sont masqués de la liste principale
              pour désencombrer la vue. Ils restent accessibles/actionnables via la
              modale "Récap par agent" (clic sur la bulle de chaque agent plus haut).
              🆕 Filtre chips supplémentaire (all/pending/authorized/today) */}
          {orders.filter(o => {
            if (o.status === "cancelled") return false;
            if (chipFilter === "pending") return o.status === "pending_manager" || o.status === "pending_director";
            if (chipFilter === "authorized") return o.status === "authorized";
            if (chipFilter === "today") return (o.created_at || "").slice(0, 10) === new Date().toISOString().slice(0, 10);
            return true;
          }).map(order => {
            const badge = STATUS_BADGE[order.status] || { label: order.status, className: "bg-slate-500/20 text-slate-300" };
            const canManagerAuth = isManager && order.status === "pending_manager";
            const canDirectorAuth = isAdmin && order.status === "pending_director";
            const canEdit = order.status === "pending_manager";
            const canCancel = (isAdmin || isManager) && order.status !== "settled";
            const isNotAuthorized = order.status === "cancelled";
            const isPendingAuth = order.status === "pending_manager" || order.status === "pending_director"; // 🆕 pas encore autorisé
            const owedAmount = order.subtotal || order.total || 0; // 🆕 non autorisé → montant DÛ = total complet des plats (sans remise)
            const cancelReason = order.cancellation_reason || order.refusal_reason || "Non autorisé";
            return (
              <Card
                key={order.id}
                className={`transition-colors ${
                  isNotAuthorized
                    ? "bg-red-950/40 border-red-500/60 hover:border-red-500 ring-1 ring-red-500/20"
                    : "bg-slate-800/40 border-slate-700 hover:border-violet-500/40"
                }`}
                data-testid={`mgr-order-${order.id}`}
              >
                <CardContent className="p-3">
                  {isNotAuthorized && (
                    <div className="mb-2 p-2.5 rounded-md bg-red-500/15 border border-red-500/50 flex items-start gap-2" data-testid={`mgr-not-authorized-${order.id}`}>
                      <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" strokeWidth={2.4} />
                      <div className="flex-1 min-w-0">
                        <p className="text-red-100 text-[13px] font-bold uppercase tracking-wide leading-tight">
                          Bon personnel NON AUTORISÉ
                        </p>
                        <p className="text-red-200 text-xs mt-0.5">
                          Motif : <span className="font-semibold text-red-100">{cancelReason}</span>
                        </p>
                        <p className="text-red-100 text-[13px] font-black mt-1">
                          Montant dû : <span className="text-white bg-red-500/40 px-2 py-0.5 rounded tabular-nums">{formatPrice(owedAmount)} F</span>
                          <span className="text-red-300 text-[10.5px] font-normal ml-2">(total complet des plats — remise NON applicable)</span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex-1 min-w-[250px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`font-bold ${isNotAuthorized ? "text-red-200" : "text-violet-300"}`}>{order.employee_name}</span>
                        <Badge className="bg-slate-700 text-slate-300 text-[10px]">{order.employee_position}</Badge>
                        <Badge className={`${badge.className} border text-[10px]`}>{badge.label}</Badge>
                        <span className="text-slate-500 text-xs">{format(new Date(order.created_at), "dd/MM HH:mm", { locale: fr })}</span>
                      </div>
                      <p className="text-slate-300 text-xs">
                        {(order.items || []).map(it => `${it.quantity}× ${it.name}`).join(" · ")}
                      </p>
                      {!isNotAuthorized && (
                        isPendingAuth ? (
                          <div className="flex flex-wrap items-center gap-3 mt-1" data-testid={`mgr-pending-price-${order.id}`}>
                            <span className="text-slate-400 text-xs">Prix normal :</span>
                            <span className="text-amber-300 font-bold text-[13.5px] tabular-nums">{formatPrice(order.subtotal)} F</span>
                            <span className="text-slate-500 text-[10.5px] italic">
                              (Remise 50% appliquée après autorisation → {formatPrice(order.total)} F)
                            </span>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-center gap-3 mt-1">
                            <span className="text-slate-400 text-xs">Sous-total : <span className="text-white">{formatPrice(order.subtotal)} F</span></span>
                            <span className="text-slate-400 text-xs">Remise 50% : <span className="text-emerald-400">-{formatPrice(order.discount_amount)} F</span></span>
                            <span className="text-violet-300 font-bold">À retenir : {formatPrice(order.total)} F</span>
                          </div>
                        )
                      )}
                      {(order.authorizations?.manager || order.authorizations?.director) && (
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {order.authorizations?.manager && (
                            <span className="text-emerald-400 text-[10px] flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> Resp. Op.: {order.authorizations.manager.name}
                            </span>
                          )}
                          {order.authorizations?.director && (
                            <span className="text-emerald-400 text-[10px] flex items-center gap-1">
                              <ShieldCheck className="w-3 h-3" /> D.G.: {order.authorizations.director.name}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-wrap justify-end">
                      {/* 🆕 Admin — validation CAS PAR CAS direct — inclut ré-autorisation des bons refusés */}
                      {isAdmin && (order.status === "pending_manager" || order.status === "pending_director" || order.status === "cancelled") && (
                        <PersonnelOrderActions
                          order={order}
                          scope="manager"
                          adminName={currentUser?.full_name || currentUser?.username || "Admin"}
                          onRefresh={fetchOrders}
                        />
                      )}
                      {canManagerAuth && (
                        <Button size="sm" onClick={() => authorize(order, "manager")} className="bg-amber-600 hover:bg-amber-700 h-7 text-xs" data-testid={`mgr-auth-manager-${order.id}`}>
                          <ShieldCheck className="w-3 h-3 mr-1" /> Auto-confirmer (Resp. Op.)
                        </Button>
                      )}
                      {canDirectorAuth && (
                        <Button size="sm" onClick={() => authorize(order, "director")} className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" data-testid={`mgr-auth-director-${order.id}`}>
                          <ShieldCheck className="w-3 h-3 mr-1" /> Autoriser (D.G.)
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(order)} className="h-7 text-slate-400 hover:bg-slate-700">
                          <Edit2 className="w-3 h-3" />
                        </Button>
                      )}
                      {canCancel && (
                        <Button size="sm" variant="ghost" onClick={() => cancelOrder(order)} className="h-7 text-red-400 hover:bg-red-500/20" data-testid={`mgr-cancel-${order.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-violet-300 flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              {editingOrder ? "Modifier la commande responsable op. & log" : "Nouvelle commande RESPONSABLE OP. & LOG"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-violet-900/15 border border-violet-500/30 rounded-lg p-3 space-y-3">
              <p className="text-violet-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Identité de la responsable op. & log (obligatoire)
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Nom de la responsable op. & log *</Label>
                  <Input
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Nom de la responsable op. & log en exercice"
                    className="bg-slate-800 border-slate-700 text-white"
                    data-testid="mgr-input-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs mb-1 block">Poste</Label>
                  <Input
                    value="Responsable Op. & Log"
                    disabled
                    className="bg-slate-800/60 border-slate-700 text-slate-300"
                  />
                </div>
              </div>

              {capStatus && (
                <div className="bg-slate-900/50 rounded-md p-2.5 text-xs">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-slate-400">Plafond mensuel ({capStatus.month}) :</span>
                    <span className={`font-bold ${capStatus.is_capped ? 'text-red-400' : 'text-emerald-300'}`}>
                      {formatPrice(capStatus.used)} / {formatPrice(maxPerMonth)} F
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${projectedUsed > maxPerMonth ? 'bg-red-500' : 'bg-violet-500'}`}
                      style={{ width: `${Math.min(100, (projectedUsed / (maxPerMonth || 1)) * 100)}%` }}
                      data-testid="mgr-cap-bar"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px]">
                    <span className="text-slate-500">Cette commande : <span className="text-violet-300">{formatPrice(totalAfter)} F</span></span>
                    {willExceedCap && (
                      <span className="text-red-400 font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Dépasse le plafond !</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Rechercher un produit</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Nom du produit..." className="bg-slate-800 border-slate-700 text-white pl-10" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-44 overflow-y-auto">
              {filteredProducts.slice(0, 12).map(p => (
                <Button key={p.id} variant="outline" onClick={() => addProduct(p)} className="h-auto py-2 px-3 border-slate-600 text-left justify-start hover:bg-violet-500/20 hover:border-violet-500" data-testid={`mgr-add-product-${p.id}`}>
                  <div className="w-full">
                    <p className="text-white text-sm font-medium truncate">{p.name}</p>
                    <p className="text-amber-400 text-xs">{formatPrice(p.price)} F</p>
                  </div>
                </Button>
              ))}
            </div>

            {selectedItems.length > 0 && (
              <div className="bg-slate-800/60 border border-slate-700 rounded-lg p-3 space-y-2">
                <p className="text-slate-400 text-xs font-bold uppercase">Commande</p>
                {selectedItems.map((it, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="ghost" onClick={() => decreaseProduct(it.product_id)} className="h-6 w-6 p-0 text-red-400 hover:bg-red-500/20"><Minus className="w-3 h-3" /></Button>
                      <span className="text-white text-sm">{it.quantity}× {it.name}</span>
                      <Button size="sm" variant="ghost" onClick={() => addProduct({ id: it.product_id, name: it.name, price: it.price })} className="h-6 w-6 p-0 text-green-400 hover:bg-green-500/20"><Plus className="w-3 h-3" /></Button>
                    </div>
                    <span className="text-amber-400 font-medium text-sm">{formatPrice(it.price * it.quantity)} F</span>
                  </div>
                ))}
                <div className="border-t border-slate-700 pt-2 space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">Sous-total</span>
                    <span className="text-white">{formatPrice(subtotal)} F</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-emerald-400">Remise (50%)</span>
                    <span className="text-emerald-400">-{formatPrice(discount)} F</span>
                  </div>
                  <div className="flex items-center justify-between text-base">
                    <span className="text-violet-300 font-bold">À retenir sur salaire</span>
                    <span className="text-violet-300 font-bold" data-testid="mgr-total-after">{formatPrice(totalAfter)} F</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Notes (optionnel)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes..." className="bg-slate-800 border-slate-700 text-white min-h-[60px]" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-400">
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={loading || willExceedCap || !employeeName.trim() || selectedItems.length === 0} className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50" data-testid="mgr-submit-btn">
              <Check className="w-4 h-4 mr-1" /> {editingOrder ? "Mettre à jour" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closure Modal */}
      <Dialog open={showClosureModal} onOpenChange={setShowClosureModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-purple-300 flex items-center gap-2">
              <Lock className="w-5 h-5" /> Clôture mensuelle — Bons RESPONSABLE OP. & LOG
            </DialogTitle>
          </DialogHeader>
          {closurePreview && (
            <div className="space-y-3">
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 text-sm">
                <p className="text-slate-300">Mois : <span className="text-white font-bold">{closurePreview.month}</span></p>
                <p className="text-slate-300">Commandes à clôturer : <span className="text-purple-300 font-bold">{closurePreview.total_count}</span></p>
                <p className="text-slate-300">Total à retenir sur salaire : <span className="text-violet-300 font-bold text-lg">{formatPrice(closurePreview.total_amount)} F</span></p>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-1.5">
                {closurePreview.by_employee.map((e, i) => (
                  <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded px-3 py-1.5">
                    <div>
                      <span className="text-white text-sm">{e.name}</span>
                      <span className="text-slate-500 text-xs ml-2">{e.position} · {e.count} cmd</span>
                    </div>
                    <span className="text-violet-300 font-bold text-sm">{formatPrice(e.total)} F</span>
                  </div>
                ))}
              </div>
              <p className="text-amber-400 text-xs flex items-start gap-1">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                Cette action est irréversible. Les commandes passeront en "Réglé sur salaire" et un PDF sera ouvert.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClosureModal(false)} className="border-slate-600 text-slate-400">
              Annuler
            </Button>
            <Button onClick={confirmClosure} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="mgr-confirm-closure-btn">
              <Lock className="w-4 h-4 mr-1" /> Confirmer la clôture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerOrdersTab;
